import React, { useState, useMemo } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Sparkles, FileText, Camera, Images } from "lucide-react";
import Header from "@/components/Header";
import { Card, TextInput, TextArea, Select, Button } from "@/components/ui-kit";
import DocViewer from "@/components/DocViewer";
import {
  listProviders, listAlbaranTemplates, createAlbaranTemplate, updateAlbaranTemplate,
  deleteAlbaranTemplate, analyzeAlbaran, uploadFile,
} from "@/lib/api";
import { resizeImage } from "@/lib/image";

const empty = {
  id: null, provider_id: "", doc_type: "albara", handwritten: false,
  has_lot: false, has_expiry: false, columns_hint: "", extraction_prompt: "", sample_image: "",
};

export default function AlbaranTemplates() {
  const { data: providers } = useSWR("providers", listProviders);
  const { data: templates, mutate } = useSWR("albaran-templates", listAlbaranTemplates);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [viewer, setViewer] = useState(null);

  const provName = (id) => providers?.find((p) => p.id === id)?.name || "—";
  const withTpl = useMemo(() => new Set((templates || []).map((t) => t.provider_id)), [templates]);
  const pending = (providers || []).filter((p) => !withTpl.has(p.id));

  const startNew = () => setEditing({ ...empty, provider_id: pending[0]?.id || providers?.[0]?.id || "" });
  const startEdit = (t) => setEditing({ ...empty, ...t });

  const handleSample = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const f = await resizeImage(file, 1600, 0.75);
      const { url } = await uploadFile(f);
      setEditing((s) => ({ ...s, sample_image: url }));
    } catch { toast.error("No s'ha pogut pujar la mostra"); }
    finally { setUploading(false); }
  };

  const analyze = async () => {
    if (!editing.sample_image) return toast.error("Puja primer una mostra de l'albarà");
    setAnalyzing(true);
    try {
      const d = await analyzeAlbaran(editing.sample_image);
      setEditing((s) => ({
        ...s,
        doc_type: d.doc_type === "factura" ? "factura" : "albara",
        handwritten: !!d.handwritten,
        has_lot: !!d.has_lot,
        has_expiry: !!d.has_expiry,
        columns_hint: Array.isArray(d.columns) ? d.columns.join(" · ") : (s.columns_hint || ""),
      }));
      toast.success("Estructura proposada per la IA. Revisa-la.");
    } catch (err) { toast.error(err.response?.data?.detail || "No s'ha pogut analitzar"); }
    finally { setAnalyzing(false); }
  };

  const save = async () => {
    if (!editing.provider_id) return toast.error("Tria un proveïdor");
    setSaving(true);
    try {
      const payload = {
        provider_id: editing.provider_id, doc_type: editing.doc_type, handwritten: editing.handwritten,
        has_lot: editing.has_lot, has_expiry: editing.has_expiry, columns_hint: editing.columns_hint,
        extraction_prompt: editing.extraction_prompt, sample_image: editing.sample_image, unit_map: {}, active: true,
      };
      if (editing.id) await updateAlbaranTemplate(editing.id, payload);
      else await createAlbaranTemplate(payload);
      toast.success("Plantilla desada"); setEditing(null); mutate();
    } catch (err) { toast.error(err.response?.data?.detail || "Error desant"); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminar la plantilla de ${provName(t.provider_id)}?`)) return;
    try { await deleteAlbaranTemplate(t.id); toast.success("Eliminada"); mutate(); }
    catch { toast.error("Error eliminant"); }
  };

  const Toggle = ({ label, k }) => (
    <button type="button" onClick={() => setEditing((s) => ({ ...s, [k]: !s[k] }))}
      className={"rounded-full border px-3 py-1.5 text-sm font-semibold transition-all " +
        (editing[k] ? "border-terracotta bg-terracotta text-white" : "border-line bg-white text-ink-soft hover:border-terracotta/40")}>
      {label}
    </button>
  );

  return (
    <>
      <Header title="Models d'albarà" />
      <div className="mx-auto max-w-2xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up flex items-center justify-between gap-3">
          <div>
            <div className="label-eyebrow">Admin · Recepció</div>
            <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">Models d'albarà</h2>
          </div>
          {!editing && <Button onClick={startNew} testid="tpl-new"><Plus size={18} /> Nou model</Button>}
        </div>
        <p className="mt-2 text-sm text-ink-soft">Cada proveïdor pot tenir el seu model perquè la IA extregui bé els albarans.</p>

        {!editing && pending.length > 0 && (
          <Card className="mt-4 bg-cream/50">
            <div className="label-eyebrow mb-1 text-terracotta">Proveïdors sense model ({pending.length})</div>
            <div className="text-sm text-ink-soft">{pending.map((p) => p.name).join(" · ")}</div>
          </Card>
        )}

        {editing && (
          <Card className="mt-6"><div className="space-y-4">
            <div><div className="label-eyebrow mb-2">Proveïdor</div>
              <Select value={editing.provider_id} onChange={(e) => setEditing((s) => ({ ...s, provider_id: e.target.value }))}>
                <option value="">— Tria —</option>
                {providers?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select></div>

            <div>
              <div className="label-eyebrow mb-2">Mostra de l'albarà</div>
              {editing.sample_image && (
                <button type="button" onClick={() => setViewer(editing.sample_image)} className="mb-2 block">
                  <img src={editing.sample_image} alt="mostra" className="h-28 rounded-xl border border-line object-cover" />
                </button>
              )}
              <div className="flex gap-2">
                <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-white px-3 py-3 text-sm font-semibold text-ink-soft hover:border-terracotta hover:text-terracotta">
                  <Camera size={16} /> {uploading ? "Pujant…" : "Càmera"}
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleSample} disabled={uploading} />
                </label>
                <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-white px-3 py-3 text-sm font-semibold text-ink-soft hover:border-terracotta hover:text-terracotta">
                  <Images size={16} /> Galeria
                  <input type="file" accept="image/*" className="hidden" onChange={handleSample} disabled={uploading} />
                </label>
              </div>
              {editing.sample_image && (
                <Button variant="secondary" onClick={analyze} disabled={analyzing} className="mt-2 w-full">
                  <Sparkles size={16} /> {analyzing ? "Analitzant…" : "Analitzar estructura (IA)"}
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><div className="label-eyebrow mb-2">Tipus document</div>
                <Select value={editing.doc_type} onChange={(e) => setEditing((s) => ({ ...s, doc_type: e.target.value }))}>
                  <option value="albara">Albarà</option><option value="factura">Factura</option>
                </Select></div>
            </div>
            <div>
              <div className="label-eyebrow mb-2">Característiques</div>
              <div className="flex flex-wrap gap-2">
                <Toggle label="Manuscrit" k="handwritten" />
                <Toggle label="Porta lot" k="has_lot" />
                <Toggle label="Porta caducitat" k="has_expiry" />
              </div>
            </div>
            <div><div className="label-eyebrow mb-2">Columnes (separades per ·)</div>
              <TextInput value={editing.columns_hint} onChange={(e) => setEditing((s) => ({ ...s, columns_hint: e.target.value }))} placeholder="Article · Descripció · Quantitat · Quil" /></div>
            <div><div className="label-eyebrow mb-2">Instruccions extra per a la IA (opcional)</div>
              <TextArea value={editing.extraction_prompt} onChange={(e) => setEditing((s) => ({ ...s, extraction_prompt: e.target.value }))}
                placeholder="Ex: la columna 'Quil' és el pes en kg. Ignora la línia d'envasos." /></div>
            <div className="flex gap-3">
              <Button onClick={save} disabled={saving || uploading} testid="tpl-save"><Check size={18} /> {saving ? "Desant…" : "Desar model"}</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}><X size={18} /> Cancel·lar</Button>
            </div>
          </div></Card>
        )}

        <div className="mt-6 space-y-3">
          {templates?.map((t) => (
            <Card key={t.id}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-display text-lg font-bold text-ink">{provName(t.provider_id)}</div>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-ink-soft">
                    <span className="rounded-full bg-cream px-2 py-0.5 font-semibold text-ink">{t.doc_type === "factura" ? "Factura" : "Albarà"}</span>
                    {t.handwritten && <span className="rounded-full bg-ember-bg px-2 py-0.5 font-semibold text-ember">Manuscrit</span>}
                    {t.has_lot && <span>· lot</span>}
                    {t.has_expiry && <span>· caducitat</span>}
                  </div>
                  {t.columns_hint && <div className="mt-1 text-xs text-ink-soft/70">{t.columns_hint}</div>}
                  {t.sample_image && <button onClick={() => setViewer(t.sample_image)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-terracotta"><FileText size={12} /> Veure mostra</button>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(t)} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-muted hover:text-ink" aria-label="Editar"><Pencil size={16} /></button>
                  <button onClick={() => remove(t)} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-ember-bg hover:text-ember" aria-label="Eliminar"><Trash2 size={16} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
      {viewer && <DocViewer url={viewer} onClose={() => setViewer(null)} />}
    </>
  );
}
